/**
 * Admin "Issue contract" modal — shared component.
 * Used from:
 *   - /admin/contracts (vault page, generic open)
 *   - /admin/users (per-row dropdown, pre-filled recipient)
 *   - /admin/publishers (Active tab, per-row button, pre-filled recipient)
 *
 * Props:
 *   open, onOpenChange — visibility
 *   onIssued()         — called after a successful issuance (parent reloads its list)
 *   onPreview(contract)— called when admin clicks the toast "View" action
 *   initialUser        — { id, email, name, company, role } to pre-fill recipient
 *   initialTemplate    — template key to default-select (e.g. "buyer_msa", "dpa")
 */
import React, { useEffect, useState } from "react";
import { Loader2, Search, X, User, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Default template for each user role
const ROLE_TO_TEMPLATE = {
  client:     "buyer_msa",
  buyer:      "buyer_msa",
  publisher:  "publisher_msa",
  affiliate:  "affiliate_msa",
  admin:      "nda",
};

export function IssueContractDialog({
  open,
  onOpenChange,
  onIssued,
  onPreview,
  initialUser = null,
  initialTemplate = null,
}) {
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(initialTemplate || "dpa");
  const [mode, setMode] = useState(initialUser ? "existing" : "existing");
  const [userSearch, setUserSearch] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(initialUser);
  const [extEmail, setExtEmail] = useState("");
  const [extName, setExtName] = useState("");
  const [extCompany, setExtCompany] = useState("");
  const [sendVia, setSendVia] = useState("email");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Load template catalogue on open
  useEffect(() => {
    if (!open) return;
    api.get("/contracts/templates")
      .then((r) => setTemplates(Array.isArray(r.data) ? r.data : r.data?.templates || []))
      .catch(() => setTemplates([]));
  }, [open]);

  // When dialog opens with a pre-filled user, sync state once
  useEffect(() => {
    if (!open) return;
    if (initialUser) {
      setMode("existing");
      setSelectedUser(initialUser);
    }
    if (initialTemplate) {
      setTemplate(initialTemplate);
    } else if (initialUser?.role && ROLE_TO_TEMPLATE[initialUser.role]) {
      setTemplate(ROLE_TO_TEMPLATE[initialUser.role]);
    }
    // intentional: only re-sync state when the dialog opens, not on prop identity change
    // eslint-disable-next-line
  }, [open]);

  // Debounced user search
  useEffect(() => {
    if (mode !== "existing") return;
    if (!userSearch || userSearch.length < 2) { setUserOptions([]); return; }
    const t = setTimeout(() => {
      api.get(`/admin/contracts/users-search?q=${encodeURIComponent(userSearch)}&limit=8`)
        .then((r) => setUserOptions(r.data?.items || []))
        .catch(() => setUserOptions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [userSearch, mode]);

  const reset = () => {
    setTemplate(initialTemplate || "dpa");
    setMode("existing");
    setUserSearch("");
    setUserOptions([]);
    setSelectedUser(initialUser || null);
    setExtEmail("");
    setExtName("");
    setExtCompany("");
    setSendVia("email");
    setNote("");
  };

  const submit = async () => {
    if (!template) return toast.error("Select a contract template");
    if (mode === "existing" && !selectedUser) return toast.error("Pick a recipient user");
    if (mode === "external" && !extEmail) return toast.error("Enter recipient email");

    setBusy(true);
    try {
      const body = {
        template,
        send_via: sendVia,
        note: note || undefined,
        ...(mode === "existing"
          ? { recipient_user_id: selectedUser.id }
          : {
              recipient_email: extEmail.trim().toLowerCase(),
              recipient_name: extName.trim(),
              recipient_company: extCompany.trim(),
            }),
      };
      const { data } = await api.post("/admin/contracts/issue", body);
      const recip = mode === "existing" ? selectedUser.email : extEmail;
      const sentBits = [];
      if (data.emailed) sentBits.push("email sent");
      if (data.sms_sent) sentBits.push("SMS sent");
      if (sendVia === "none") sentBits.push("no notifications sent");
      const issued = data.contract;
      toast.success(
        `Contract issued to ${recip} · ${sentBits.join(" · ") || "queued"}`,
        issued && onPreview
          ? {
              action: {
                label: "View",
                onClick: () =>
                  onPreview({
                    id: issued.id,
                    template_title: issued.template_title || issued.template,
                  }),
              },
            }
          : undefined,
      );
      reset();
      onOpenChange(false);
      onIssued?.();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        toast.error("Your session expired — please sign in again.");
        setTimeout(() => { window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`; }, 1500);
      } else {
        toast.error(formatApiError(e?.response?.data?.detail) || "Could not issue contract");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="issue-contract-dialog">
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-[0_30px_80px_rgba(15,23,42,0.25)] border border-slate-200 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#0055FF]">New contract</div>
            <div className="text-lg font-semibold text-slate-900 tracking-tight">
              {initialUser ? `Issue a contract to ${initialUser.name || initialUser.email}` : "Issue a contract manually"}
            </div>
          </div>
          <button onClick={() => { reset(); onOpenChange(false); }} className="p-2 rounded-full hover:bg-slate-100" data-testid="issue-contract-close"><X className="size-4 text-slate-500" /></button>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto">
          {/* Template */}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Template</label>
            <select
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-[13px] bg-white"
              data-testid="issue-template"
            >
              {templates.map((t) => (
                <option key={t.key} value={t.key}>{t.title}</option>
              ))}
            </select>
            <p className="text-[11.5px] text-slate-500 mt-1.5">
              {templates.find((t) => t.key === template)?.subtitle || "Choose the contract type"}
            </p>
          </div>

          {/* Recipient mode toggle (hidden when pre-filled) */}
          {!initialUser && (
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Recipient</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setMode("existing")}
                  className={`text-left rounded-lg border px-3 py-2 transition-colors ${mode === "existing" ? "border-[#0055FF] bg-[#0055FF]/5" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid="issue-mode-existing"
                >
                  <div className="flex items-center gap-2">
                    <User className="size-3.5 text-slate-700" />
                    <span className="text-[12.5px] font-medium text-slate-900">Existing portal user</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">Search by email, name, company</p>
                </button>
                <button
                  onClick={() => setMode("external")}
                  className={`text-left rounded-lg border px-3 py-2 transition-colors ${mode === "external" ? "border-[#0055FF] bg-[#0055FF]/5" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid="issue-mode-external"
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="size-3.5 text-slate-700" />
                    <span className="text-[12.5px] font-medium text-slate-900">External recipient</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">Vendor, prospect, partner — no account yet</p>
                </button>
              </div>
            </div>
          )}

          {/* Recipient picker */}
          {mode === "existing" ? (
            <div className="space-y-2">
              {selectedUser ? (
                <div className="rounded-lg border border-[#0055FF]/40 bg-[#0055FF]/5 px-3 py-2.5 flex items-start justify-between gap-3" data-testid="issue-selected-user">
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-slate-900 truncate">{selectedUser.name || selectedUser.email}</div>
                    <div className="text-[11.5px] text-slate-500 truncate">{selectedUser.email}{selectedUser.company ? ` · ${selectedUser.company}` : ""}</div>
                    <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-slate-500 mt-0.5">{selectedUser.role}</div>
                  </div>
                  {!initialUser && (
                    <button onClick={() => { setSelectedUser(null); setUserSearch(""); }} className="text-[12px] text-slate-500 hover:text-slate-900" data-testid="issue-clear-user">Change</button>
                  )}
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-slate-400" />
                    <Input
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search by email, name or company…"
                      className="pl-9 h-10 text-[13px]"
                      data-testid="issue-user-search"
                    />
                  </div>
                  {userOptions.length > 0 && (
                    <div className="rounded-lg border border-slate-200 bg-white max-h-44 overflow-y-auto divide-y divide-slate-100">
                      {userOptions.map((u) => (
                        <button
                          key={u.id}
                          onClick={() => { setSelectedUser(u); setUserOptions([]); }}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors"
                          data-testid={`issue-user-opt-${u.id.slice(0, 8)}`}
                        >
                          <div className="text-[13px] text-slate-900 font-medium">{u.name || u.email}</div>
                          <div className="text-[11.5px] text-slate-500">{u.email}{u.company ? ` · ${u.company}` : ""}<span className="ml-1.5 text-[10px] font-mono uppercase tracking-[0.14em]">{u.role}</span></div>
                        </button>
                      ))}
                    </div>
                  )}
                  {userSearch.length >= 2 && userOptions.length === 0 && (
                    <div className="text-[12px] text-slate-500 px-1">No users match. Try external recipient mode.</div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11.5px] font-medium text-slate-600 mb-1">Name</label>
                  <Input value={extName} onChange={(e) => setExtName(e.target.value)} placeholder="Jane Smith" className="h-10 text-[13px]" data-testid="issue-ext-name" />
                </div>
                <div>
                  <label className="block text-[11.5px] font-medium text-slate-600 mb-1">Company</label>
                  <Input value={extCompany} onChange={(e) => setExtCompany(e.target.value)} placeholder="Vendor Ltd" className="h-10 text-[13px]" data-testid="issue-ext-company" />
                </div>
              </div>
              <div>
                <label className="block text-[11.5px] font-medium text-slate-600 mb-1">Email <span className="text-rose-500">*</span></label>
                <Input value={extEmail} onChange={(e) => setExtEmail(e.target.value)} type="email" placeholder="jane@vendor.com" className="h-10 text-[13px]" data-testid="issue-ext-email" />
              </div>
            </div>
          )}

          {/* Send via */}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Send via</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "email",     l: "Email only",         d: "Branded sign link" },
                { v: "sms_email", l: "SMS + email",        d: "Where phone known" },
                { v: "none",      l: "Don't send",         d: "Issue silently" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setSendVia(opt.v)}
                  className={`text-left rounded-lg border px-3 py-2 transition-colors ${sendVia === opt.v ? "border-[#0055FF] bg-[#0055FF]/5" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`issue-send-${opt.v}`}
                >
                  <div className="text-[12.5px] font-medium text-slate-900">{opt.l}</div>
                  <p className="text-[11px] text-slate-500 mt-0.5">{opt.d}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Optional admin note */}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Internal note <span className="font-normal text-slate-400">(optional)</span></label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. requested by Marcus during partnership call 4/Feb"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-[13px] min-h-[60px] resize-y"
              data-testid="issue-note"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3">
          <p className="text-[11.5px] text-slate-500 leading-tight">Recipient signs in-portal · automated SMS/email flows are not affected.</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} className="rounded-md h-10">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="rounded-md h-10 bg-[#0055FF] hover:bg-[#0044CC] text-white min-w-[120px]" data-testid="issue-submit">
              {busy ? <Loader2 className="size-4 animate-spin" /> : "Issue contract"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
